import Accordion from "@/common/components/Accordion";
import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import SelectController from "@/common/components/SelectController";
import CreateMaintenanceSpecialtyModal from "./CreateMaintenanceSpecialtyModal";
import { useFormContext } from "react-hook-form";
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "../../schemas/update.schema";
import { useEstablishmentInfrastructures } from "@/common/hooks/queries/useEstablishmentInfrastructures";
import { Mapper } from "@/common/services/mapper";
import { EstablishmentInfrastructuresTransformer } from "@/features/maintenance/establishmentInfrastructures/services/establishmentInfrastructuresTransformer";
import { useEstablishmentTechnicalProfessionals } from "@/common/hooks/queries/useEstablishmentTechnicalProfessionals";
import { EstablishmentTechnicalProfessionalsTransformer } from "@/features/maintenance/establishmentTechnicalProfessionals/services/establishmentTechnicalProfessionalsTransformer";
import useDebounce from "@/common/hooks/useDebounce";
import { EstablishmentLeadData } from "../../types/establishmentLead.type";
import { BaseSyntheticEvent } from "react";
import { XIcon } from "lucide-react";
import { useDeleteEstablishmentLeadMaintenanceSpecialty } from "../../hooks/useDeleteEstablishmentLeadMaintenanceSpecialty";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";

interface MaintenanceGeneralInformationSectionProps {
  establishmentLead: EstablishmentLeadData;
  submitLead?: (e?: BaseSyntheticEvent) => Promise<void>;
  submitMaintenanceModels?: (e?: BaseSyntheticEvent) => Promise<void>;
  disabled?: boolean;
}

const MaintenanceGeneralInformationSection = ({
  establishmentLead,
  submitLead,
  submitMaintenanceModels,
  disabled,
}: MaintenanceGeneralInformationSectionProps) => {
  const {
    control,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useFormContext<EstablishmentLeadsFunnelUpdateValidationSchema>();

  const queryClient = useQueryClient();

  const [searchEstablishmentInfraestructures, setSearchEstablishmentInfraestructures] = useDebounce('');
  const [searchEstablishmentTechnicalProfessionals, setSearchEstablishmentTechnicalProfessionals] = useDebounce('');

  const {
    data: establishmentInfrastructuresOptions,
  } = useEstablishmentInfrastructures({
    filters: {
      search: searchEstablishmentInfraestructures,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentInfrastructuresTransformer.optionsTransformer(),
    }),
  });

  const {
    data: establishmentTechnicalProfessionals,
  } = useEstablishmentTechnicalProfessionals({
    filters: {
      search: searchEstablishmentTechnicalProfessionals,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentTechnicalProfessionalsTransformer.optionsTransformer(),
    }),
  });

  const {
    mutate: deleteEstablishmentLeadMaintenanceSpecialty,
    isPending: deleteEstablishmentLeadMaintenanceSpecialtyIsPending,
  } = useDeleteEstablishmentLeadMaintenanceSpecialty({
    id: establishmentLead.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLead.id] });
    },
    onError: FormTransformers.errorsTransformer<EstablishmentLeadsFunnelUpdateValidationSchema>({
      setError
    }),
  });

  const handleClearMaintenanceSpecialty = () => {
    if (establishmentLead.maintenance_specialty) {
      deleteEstablishmentLeadMaintenanceSpecialty(establishmentLead.maintenance_specialty.id);
    }
  }

  const maintenanceSpecialty = establishmentLead.maintenance_specialty;

  return (
    <Accordion
      defaultOpen
      head="Informações gerais"
      className="p-4"
      triggerClassName="px-0 py-1 text-[18px] font-bold"
      bodyClassName="pt-4 pb-0"
      body={(
        <div className="flex flex-col gap-4">
          <SelectController
            label="Infraestrutura para manutenção"
            name="maintenance_infrastructures"
            control={control}
            options={establishmentInfrastructuresOptions}
            onInputChange={setSearchEstablishmentInfraestructures}
            isMulti
            markAsRequired
            triggerOnChange={submitMaintenanceModels}
            isDisabled={disabled}
            errorMessage={errors?.maintenance_infrastructures?.message as string}
            operationKlassToCheckPermission="Admin::Maintenance::EstablishmentInfrastructures::Filter"
            optionLabelFormat="icon"
          />

          <SelectController
            label="Equipe técnica"
            name="maintenance_technical_professionals"
            control={control}
            options={establishmentTechnicalProfessionals}
            onInputChange={setSearchEstablishmentTechnicalProfessionals}
            isMulti
            markAsRequired
            triggerOnChange={submitMaintenanceModels}
            isDisabled={disabled}
            errorMessage={errors?.maintenance_technical_professionals?.message as string}
            operationKlassToCheckPermission="Admin::Maintenance::EstablishmentTechnicalProfessionals::Filter"
            optionLabelFormat="icon"
          />

          <div className="flex flex-col gap-1.5">
            <p className="text-xs">
              Realiza coleta/guincho de equipamento?
            </p>

            <div className="flex items-center gap-2">
              <Checkbox
                type="radio"
                name="tow_service_available"
                label="Sim"
                className="items-center gap-1.5"
                boxClassName="rounded-full size-6"
                checked={!!watch?.('tow_service_available')}
                onChange={(event) => {
                  if (event.currentTarget.checked) {
                    setValue('tow_service_available', true);
                    submitLead?.();
                  }
                }}
              />

              <Checkbox
                type="radio"
                name="tow_service_available"
                label="Não"
                className="items-center gap-1.5"
                boxClassName="rounded-full size-6"
                checked={!watch?.('tow_service_available')}
                onChange={(event) => {
                  if (event.currentTarget.checked) {
                    setValue('tow_service_available', false);
                    submitLead?.();
                  }
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-base">
              Especialidade
            </p>

            {maintenanceSpecialty && (
              <div className="bg-white-100 p-4 rounded-lg flex items-center gap-2">
                <Button
                  variant="link-white"
                  onClick={handleClearMaintenanceSpecialty}
                  disabled={disabled}
                  isLoading={deleteEstablishmentLeadMaintenanceSpecialtyIsPending}
                  spinnerClassName="size-5"
                >
                  {!deleteEstablishmentLeadMaintenanceSpecialtyIsPending && <XIcon strokeWidth={1} size={20} className="text-danger" />}
                </Button>

                <div className="flex items-center gap-2">
                  <div className="bg-white-100 p-2 rounded-md">
                    <p className="text-xs font-bold">
                      {maintenanceSpecialty.system_part.system.display_name}
                    </p>
                  </div>

                  <div className="flex flex-col items-start">
                    <p className="font-bold">
                      {maintenanceSpecialty.action.display_name}

                      &nbsp;

                      <span className="font-normal">
                        em
                      </span>

                      &nbsp;

                      {maintenanceSpecialty.system_part.part.display_name}
                    </p>

                    <p className="text-xs">
                      {maintenanceSpecialty.action.description}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!maintenanceSpecialty && (
              <CreateMaintenanceSpecialtyModal
                establishmentLead={establishmentLead}
                disabled={disabled}
              />
            )}
          </div>
        </div>
      )}
    />
  );
}

export default MaintenanceGeneralInformationSection;
